import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent } from 'react';

const DEFAULT_VIEW_MARGIN_PX = 16;

export type FloatingPanelInitialPosition = 'topLeft' | 'bottomRight';

const INTERACTIVE_DRAG_SKIP_SELECTOR =
  'button, input, textarea, select, a, [role="button"], label, [contenteditable="true"]';

export interface UseFloatingPanelDragOptions {
  enabled: boolean;
  viewMarginPx?: number;
  initialPosition?: FloatingPanelInitialPosition;
  /** Класс на шапке во время перетаскивания */
  draggingHeaderClassName?: string;
  /** Перетаскивание только при клике в верхней зоне панели (px от верха shellRef) */
  dragZoneTopPx?: number;
}

export function useFloatingPanelDrag({
  enabled,
  viewMarginPx = DEFAULT_VIEW_MARGIN_PX,
  initialPosition = 'topLeft',
  draggingHeaderClassName = 'floating-panel-drag-header_dragging',
  dragZoneTopPx,
}: UseFloatingPanelDragOptions) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ left: viewMarginPx, top: viewMarginPx });
  const dragRef = useRef<{ active: boolean; offsetX: number; offsetY: number }>({
    active: false,
    offsetX: 0,
    offsetY: 0,
  });

  const clampPosition = useCallback(
    (left: number, top: number) => {
      const shell = shellRef.current;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = shell?.offsetWidth ?? 360;
      const h = shell?.offsetHeight ?? 320;
      const maxLeft = Math.max(viewMarginPx, vw - w - viewMarginPx);
      const maxTop = Math.max(viewMarginPx, vh - h - viewMarginPx);
      return {
        left: Math.min(Math.max(viewMarginPx, left), maxLeft),
        top: Math.min(Math.max(viewMarginPx, top), maxTop),
      };
    },
    [viewMarginPx]
  );

  const resolveInitialPosition = useCallback(() => {
    const shell = shellRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = shell?.offsetWidth ?? 360;
    const h = shell?.offsetHeight ?? 320;
    if (initialPosition === 'bottomRight') {
      return clampPosition(vw - w - viewMarginPx, vh - h - viewMarginPx);
    }
    return clampPosition(viewMarginPx, viewMarginPx);
  }, [clampPosition, initialPosition, viewMarginPx]);

  useLayoutEffect(() => {
    if (!enabled) return;
    setPosition(resolveInitialPosition());
  }, [enabled, resolveInitialPosition]);

  useEffect(() => {
    if (!enabled) return;
    const onResize = () => {
      setPosition((p) => clampPosition(p.left, p.top));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled, clampPosition]);

  const handleHeaderPointerDown = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      if ((e.target as HTMLElement).closest(INTERACTIVE_DRAG_SKIP_SELECTOR)) return;
      const shell = shellRef.current;
      if (dragZoneTopPx != null && shell) {
        const offsetTop = e.clientY - shell.getBoundingClientRect().top;
        if (offsetTop > dragZoneTopPx) return;
      }
      const t = e.currentTarget;
      t.setPointerCapture(e.pointerId);
      dragRef.current = {
        active: true,
        offsetX: e.clientX - position.left,
        offsetY: e.clientY - position.top,
      };
      t.classList.add(draggingHeaderClassName);
    },
    [dragZoneTopPx, draggingHeaderClassName, position.left, position.top]
  );

  const handleHeaderPointerMove = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      if (!dragRef.current.active) return;
      const nextLeft = e.clientX - dragRef.current.offsetX;
      const nextTop = e.clientY - dragRef.current.offsetY;
      setPosition(clampPosition(nextLeft, nextTop));
    },
    [clampPosition]
  );

  const endDrag = useCallback((e: PointerEvent<HTMLElement>) => {
    const t = e.currentTarget;
    if (t.hasPointerCapture(e.pointerId)) {
      t.releasePointerCapture(e.pointerId);
    }
    dragRef.current.active = false;
    t.classList.remove(draggingHeaderClassName);
  }, [draggingHeaderClassName]);

  const headerDragProps = {
    onPointerDown: handleHeaderPointerDown,
    onPointerMove: handleHeaderPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  } as const;

  return {
    shellRef,
    position,
    clampPosition,
    headerDragProps,
  };
}
